import { axios } from '@complispace/cs-design-system';
import jwtDecode from 'jwt-decode';
import parseDomain from 'parse-domain';

const APP_NAME = 'app_name';
const TOKEN_KEY = 'id_token';
const TOKEN_EXPIRY_KEY = 'id_token_expiry';
const USERROLE_KEY = 'id_userRole';
const ORIGINAL_ADMIN_ROLE_KEY = 'id_original_adminRole';
const FEATURE_TOGGLE = 'feature_toggle';
const PROFILE = 'profile';
const TOKENEXPIRYMS = 650000; // 10 minutes.
const DRAFT_MODE = 'draft_mode';

const appNameUrl = `${process.env.CONTENT_ENGINE_URL}/app`;

export default {
  tokenExpiryMS() {
    return TOKENEXPIRYMS;
  },

  isLoggedIn() {
    // FIXME: Vote to instantiate sessionManager as a singleton
    // prior to instanting Vue.  This would eliminate this...
    //
    // This can't be async using our current startup sequence is
    // 1. Vue instantiation
    // 2. router instantiation, beforeEach and nextTick function
    // 3. App.vue instantiation, beforeCreate dispatches session/Initialise
    // An await will yield to the router beforeEach, will always
    // see user as not logged in
    const token = localStorage.getItem(TOKEN_KEY);
    const role = localStorage.getItem(USERROLE_KEY);

    if (!!token && !!role) {
      const checkIfAuthorizationTokenValid = (encodedToken) => {
        let tokenValid = false;

        if (encodedToken) {
          const decodedToken = jwtDecode(encodedToken);
          tokenValid = decodedToken.exp * 1000 - new Date().getTime() > this.tokenExpiryMS();
        }

        // if (!tokenValid) clear();
        return tokenValid;
      };
      return checkIfAuthorizationTokenValid(token);
    }
    return false;
  },

  getAppNameFromLocalStorage() {
    return localStorage.getItem(APP_NAME);
  },

  getUserRoleFromLocalStorage() {
    return localStorage.getItem(USERROLE_KEY);
  },

  getOriginalAdminRoleFromLocalStorage() {
    return localStorage.getItem(ORIGINAL_ADMIN_ROLE_KEY);
  },

  getTokenFromLocalStorage() {
    return localStorage.getItem(TOKEN_KEY);
  },

  getTokenExpiryFromLocalStorage() {
    return localStorage.getItem(TOKEN_EXPIRY_KEY);
  },

  getDraftModeFromLocalStorage() {
    return localStorage.getItem(DRAFT_MODE);
  },

  getTokenFromUri(uri) {
    return uri.match(/#(?:access_token)=([\S\s]*?)&/)[1];
  },

  saveAppName(appName) {
    localStorage.setItem(APP_NAME, appName);
  },

  saveDraftMode(draftMode) {
    localStorage.setItem(DRAFT_MODE, draftMode);
  },

  saveToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
    const decodedToken = jwtDecode(token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, decodedToken.exp * 1000);
  },

  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  },

  saveUserRole(userRole) {
    localStorage.setItem(USERROLE_KEY, userRole);
  },

  saveOriginalAdminRole(adminRole) {
    localStorage.setItem(ORIGINAL_ADMIN_ROLE_KEY, adminRole);
  },

  isTokenExpired() {
    if (!this.isLoggedIn()) {
      throw new Error('Not logged in.');
    }
  },

  getIdpRedirectUrl() {
    return `${window.location.origin}/login/callback/idp`;
  },

  getSubdomain() {
    const domains = parseDomain(window.location.hostname, { customTlds: ['localhost'] });
    const subdomains = domains.subdomain.split('.');

    return subdomains[0];
  },

  getXSiteUri() {
    return window.location.hostname;
  },

  getXSiteUrl() {
    if (process.env.NODE_ENV === 'development')
      return process.env.X_SITE_URL_MOCK || `https://${this.getXSiteUri()}`;
    return `https://${this.getXSiteUri()}`;
  },

  getFQDN() {
    return window.location.hostname;
  },

  async getAppName() {
    try {
      const response = await axios.get(appNameUrl, {
        headers: {
          'x-site-uri': `${this.getXSiteUrl()}`
        }
      });
      return response.data.appName;
    } catch (err) {
      throw new Error('App not found');
    }
  },

  async getIdpUri(xSiteUri) {
    const idpAxios = axios.create({
      transformRequest: [
        (data, headers) => {
          /* eslint-disable no-param-reassign */
          delete headers['x-site-uri'];
          /* eslint-enable no-param-reassign */
          return data;
        }
      ]
    });
    const url = `https://idp.complispace.me/api/discovery?q=${encodeURIComponent(xSiteUri)}`;
    try {
      const response = await idpAxios.get(url);
      return response.data.results[0].fqdn;
    } catch (err) {
      window.location.href = process.env.IDP_ERROR_REDIRECT_URI;
      throw new Error(`No matching idp for ${xSiteUri}`);
    }
  },

  async init() {
    const idpUri = await this.getIdpUri(this.getXSiteUri());

    return { idpUri };
  },

  setAuthorizationHeader(uri) {
    const token = this.getTokenFromUri(uri);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  },

  setAdditionalHeaders() {
    axios.defaults.headers.common['x-site-uri'] = `${this.getXSiteUrl()}`;
  },

  deleteAdditionalHeaders() {
    delete axios.defaults.headers.common['x-site-uri'];
  },

  clear() {
    localStorage.removeItem(APP_NAME);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(USERROLE_KEY);
    localStorage.removeItem(ORIGINAL_ADMIN_ROLE_KEY);
    localStorage.removeItem(FEATURE_TOGGLE);
    localStorage.removeItem(DRAFT_MODE);
    this.clearHeaders();
  },

  clearHeaders() {
    this.deleteAdditionalHeaders();
    delete axios.defaults.headers.common.Authorization;
  },

  async saveProfile(idpUri) {
    const userInfo = await this.getAuthenticationFromServer(idpUri);
    if (userInfo && userInfo.data && userInfo.data.id) {
      const { displayName, name, emails } = userInfo.data;
      const profile = {
        displayName,
        name,
        email: emails[0].value.trim()
      };

      localStorage.setItem(PROFILE, JSON.stringify(profile));
    }
  },

  getProfile() {
    return localStorage.getItem(PROFILE);
  },

  async getAuthenticationFromServer(idpUri) {
    let userInfoUrl = `https://${idpUri}/api/userinfo`;
    if (idpUri.slice(-1) === '/') {
      userInfoUrl = `https://${idpUri.slice(0, -1)}/api/userinfo`;
    }

    try {
      const response = await axios.get(userInfoUrl);
      return response;
    } catch (err) {
      throw new Error('Cannot retrieve User information');
    }
  }
};
